/**
 * BB Defend — call / 3-bet / fold from the Big Blind vs an open.
 */

import { PlayingCard } from "@/features/chat/components/PlayingCard";
import { Brain } from "@/shared/components/icons";
import { QuizLayout } from "@/features/quiz/components/QuizLayout";
import { QuizOption } from "@/features/quiz/components/QuizOption";
import { RevealPanel } from "@/features/quiz/components/RevealPanel";
import { useQuizSession } from "@/features/quiz/lib/useQuizSession";
import {
  type Action,
  type BbDefendQuestion,
  generateQuestion,
} from "@/features/quiz/lib/bbDefendQuiz";
import { parseHand } from "@/features/quiz/lib/preflopRanges";

const ACTIONS: Action[] = ["fold", "call", "3bet"];
const ACTION_LABELS: Record<Action, string> = {
  fold: "Fold",
  call: "Call",
  "3bet": "3-bet",
};

function cardsForHand(hand: string): [string, string] {
  const parsed = parseHand(hand);
  if (!parsed) return ["Ah", "Ah"];
  if (parsed.isPair) return [`${parsed.high}s`, `${parsed.low}h`];
  if (parsed.suited) return [`${parsed.high}s`, `${parsed.low}s`];
  return [`${parsed.high}s`, `${parsed.low}h`];
}

export function QuizBbDefend() {
  const { question, revealedAnswer, isCorrect, correctAnswer, stats, submit, next } =
    useQuizSession<BbDefendQuestion, Action>({
      quizType: "bb_defend",
      generate: generateQuestion,
      getCorrectAnswer: (q) => q.action,
      getQuestionPayload: (q) => ({ hand: q.hand, opener: q.openerPosition }),
    });

  const cards = cardsForHand(question.hand);
  const rationale =
    correctAnswer === "3bet"
      ? `${question.hand} is in the BB's value-3-bet range: QQ+, AK go in for value vs any opener. Flatting cedes initiative; 3-betting builds the pot with the best hand.`
      : correctAnswer === "call"
      ? `${question.hand} is a defend with playability vs a ${question.openerPosition} open: pairs set-mine, suited broadways flop equity, suited connectors have nut potential. Calling realizes equity at the BB discount.`
      : `${question.hand} folds vs a ${question.openerPosition} open: dominated offsuit holdings bleed money out of position against a tighter range. The BB discount doesn't rescue everything.`;

  return (
    <QuizLayout
      Icon={Brain}
      title="BB Defend"
      subtitle="Call / 3-bet / fold from the Big Blind"
      description="Tests BB defending vs an open. Defend ~40% vs BTN, ~30% vs CO, ~20% vs UTG (sheet 1). The 3-bet range stays narrow (QQ+, AK); calling range widens against later opens. Borderline hands like AJo and KQo flip from fold-vs-early to call-vs-late: that's the lesson the curated scenarios stress."
      stats={stats}
    >
      <div className="flex flex-col items-center gap-4 py-2">
        <div className="text-sm text-text-muted">
          Hero in <strong className="text-text">BB</strong>. <strong className="text-text">{question.openerPosition}</strong> opens 2.5×.
        </div>
        <div className="flex items-center gap-2">
          <PlayingCard token={cards[0]} size="lg" />
          <PlayingCard token={cards[1]} size="lg" />
        </div>
        <div className="text-xs text-text-muted font-mono">{question.hand}</div>
      </div>

      <p className="text-center text-text text-base">Your action?</p>

      <div className="grid grid-cols-3 gap-3">
        {ACTIONS.map((act) => (
          <QuizOption
            key={act}
            value={act}
            revealedAnswer={revealedAnswer}
            correctAnswer={correctAnswer}
            onSelect={submit}
          >
            {ACTION_LABELS[act]}
          </QuizOption>
        ))}
      </div>

      {revealedAnswer !== null && (
        <RevealPanel
          isCorrect={isCorrect}
          answerLine={
            <>
              Answer: <strong>{ACTION_LABELS[correctAnswer]}</strong>
            </>
          }
          rationale={rationale}
          onNext={next}
        />
      )}
    </QuizLayout>
  );
}
